import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import CompleteTaskModal from './CompleteTaskModal'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../contexts/LanguageContext'
import { DragDropContext, Droppable, Draggable, DropResult } from '@hello-pangea/dnd'
import { 
  CalendarIcon,
  CustomersIcon,
  PlusIcon
} from '../components/Icons'
import { parseNaturalLanguageDate, formatDateForAPI } from '../utils/taskUtils'
import { makeApiCall, makeJsonApiCall } from '../utils/customerUtils'
import ArchivedTasks from './ArchivedTasks'

interface Task {
  id: number
  customer_id: number | null
  deal_id?: number
  lead_id?: number
  description: string
  due_date: string
  due_time?: string | null
  has_specific_time?: boolean
  status: 'pending' | 'in_progress' | 'completed' | 'cancelled'
  priority: 'low' | 'medium' | 'high' | 'urgent'
  customer_name: string | null
  customer_email?: string
  lead_first_name?: string | null
  lead_last_name?: string | null
  lead_email?: string | null
  deal_name?: string | null
  created_at: string
  updated_at: string
}

interface Customer {
  id: number
  name: string
  email?: string
  phone?: string
}

interface Deal {
  id: number
  name: string
  customer_name: string
}

interface Lead {
  id: number
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  description: string | null
  status_name: string
}

interface TaskColumn {
  id: string
  title: string
  date: Date
  tasks: Task[]
}

interface CompletionModal {
  isOpen: boolean
  task: Task | null
}

const TasksBoard: React.FC = () => {
  const { t } = useTranslation()
  const { language } = useLanguage()
  const navigate = useNavigate()

  // View mode state - Load from localStorage or default to 'board'
  const [viewMode, setViewMode] = useState<'board' | 'table' | 'calendar'>(() => {
    const savedViewMode = localStorage.getItem('tasks_view_mode')
    return (savedViewMode === 'table' || savedViewMode === 'board' || savedViewMode === 'calendar') ? savedViewMode : 'board'
  })
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)
  const [dateTasksModal, setDateTasksModal] = useState<{ isOpen: boolean; date: Date | null; tasks: Task[] }>({
    isOpen: false,
    date: null,
    tasks: []
  })

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('tasks_view_mode', viewMode)
  }, [viewMode])

  // Helper function to translate priority names
  const translatePriority = (priority: string) => {
    return t(`tasks.priorities.${priority}`)
  }

  // Helper function to format date with proper French translations
  const formatDateWithTranslation = (date: Date) => {
    if (language === 'fr') {
      const weekday = date.toLocaleDateString('en-US', { weekday: 'short' }).toLowerCase()
      const month = date.toLocaleDateString('en-US', { month: 'short' }).toLowerCase()
      const day = date.getDate()
      
      const translatedWeekday = t(`dashboard.dateFormats.weekdays.short.${weekday}`)
      const translatedMonth = t(`dashboard.dateFormats.months.short.${month}`)
      
      return `${translatedWeekday} ${day} ${translatedMonth}`
    } else {
      return date.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })
    }
  }
  // Helper function to format date as local YYYY-MM-DD string
  const formatLocalDate = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
  }

  // Initialize columns immediately to prevent race conditions
  const getInitialColumns = (): TaskColumn[] => {
    const today = new Date()
    const newColumns: TaskColumn[] = []
    
    for (let i = 0; i < 6; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)
      
      let title = ''
      if (i === 0) title = t('dashboard.periods.today')
      else if (i === 1) title = t('tasks.tomorrow')
      else title = formatDateWithTranslation(date)
      
      newColumns.push({
        id: `day-${i}`,
        title,
        date,
        tasks: []
      })
    }
    
    return newColumns
  }

  const [columns, setColumns] = useState<TaskColumn[]>(getInitialColumns())
  const [laterTasks, setLaterTasks] = useState<Task[]>([])
  const [showLaterExpanded, setShowLaterExpanded] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [completionModal, setCompletionModal] = useState<CompletionModal>({ isOpen: false, task: null })
  const [customers, setCustomers] = useState<Customer[]>([])
  const [deals, setDeals] = useState<Deal[]>([])
  const [leads, setLeads] = useState<Lead[]>([])
  
  // Task creation modal state
  const [showCreateTaskModal, setShowCreateTaskModal] = useState(false)
  
  // Lead detail modal (navigate fallback for lead-type tasks)
  const [leadDetailModal, setLeadDetailModal] = useState<{ isOpen: boolean, leadId: number | null }>({ isOpen: false, leadId: null })
  const [leadDetailData, setLeadDetailData] = useState<any>(null)
  const [leadDetailLoading, setLeadDetailLoading] = useState(false)
  const [leadDetailError, setLeadDetailError] = useState<string | null>(null)
  const [customerSearch, setCustomerSearch] = useState('')
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null)
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null)
  const [customerDeals, setCustomerDeals] = useState<Deal[]>([])
  const [selectedDeal, setSelectedDeal] = useState<Deal | null>(null)
  const [taskDescription, setTaskDescription] = useState('')
  const [taskPriority, setTaskPriority] = useState<'low' | 'medium' | 'high'>('medium')
  const [taskDueDate, setTaskDueDate] = useState<string>('')
  const [customDate, setCustomDate] = useState('')
  const [taskTime, setTaskTime] = useState('')
  const [hasSpecificTime, setHasSpecificTime] = useState(false)


  // Archived tasks modal state
  const [showArchivedTasks, setShowArchivedTasks] = useState(false)

  useEffect(() => {
    fetchTasks()
    fetchCustomersAndDeals()
  }, [])


  // Fetch lead details when lead detail modal opens
  useEffect(() => {
    const fetchLeadDetails = async () => {
      if (!leadDetailModal.isOpen || !leadDetailModal.leadId) {
        return
      }

      try {
        setLeadDetailLoading(true)
        setLeadDetailError(null)
        setLeadDetailData(null)

        const token = localStorage.getItem('token')
        if (!token) {
          throw new Error('Authentication required')
        }

        const response = await makeApiCall(`api/leads/${leadDetailModal.leadId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })

        if (!response.ok) {
          throw new Error('Failed to fetch lead details')
        }

        const leadData = await response.json()
        setLeadDetailData(leadData)
      } catch (err) {
        console.error('Error fetching lead details:', err)
        setLeadDetailError(err instanceof Error ? err.message : 'Failed to load lead details')
      } finally {
        setLeadDetailLoading(false)
      }
    }

    fetchLeadDetails()
  }, [leadDetailModal.isOpen, leadDetailModal.leadId])

  const initializeColumns = () => {
    // Columns are now initialized in useState, but keep this for manual refresh
    setColumns(getInitialColumns())
  }

  const fetchTasks = async () => {
    try {
      setLoading(true)
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication required')
      }

      const response = await makeApiCall('api/tasks', {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (!response.ok) {
        throw new Error('Failed to fetch tasks')
      }

      const data = await response.json()
      organizeTasks(data.tasks || [])
    } catch (err) {
      console.error('Error fetching tasks:', err)
      setError(err instanceof Error ? err.message : 'Failed to load tasks')
    } finally {
      setLoading(false)
    }
  }

  const fetchCustomersAndDeals = async () => {
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const [customersRes, dealsRes, leadsRes] = await Promise.all([
        makeApiCall('api/customers', { headers: { 'Authorization': `Bearer ${token}` } }),
        makeApiCall('api/deals', { headers: { 'Authorization': `Bearer ${token}` } }),
        makeApiCall('api/leads', { headers: { 'Authorization': `Bearer ${token}` } })
      ])

      if (customersRes.ok) {
        const data = await customersRes.json()
        setCustomers(data.customers || [])
      }

      if (dealsRes.ok) {
        const data = await dealsRes.json()
        setDeals(data.deals || [])
      }

      if (leadsRes.ok) {
        const data = await leadsRes.json()
        setLeads(data.leads || [])
      }
    } catch (err) {
      console.error('Error fetching customers, deals, and leads:', err)
    }
  }

  const organizeTasks = (tasks: Task[]) => {
    // Work with a fresh copy of columns
    const workingColumns = [...columns]
    const laterTasksArray: Task[] = []

    // Reset all column tasks
    workingColumns.forEach(col => { col.tasks = [] })

    // Get today's date string in YYYY-MM-DD format for consistent comparison
    const today = new Date()
    const todayString = formatLocalDate(today)

    const priorityRank: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 }
    const sortedTasks = [...tasks].sort((a, b) =>
      (priorityRank[a.priority] ?? 9) - (priorityRank[b.priority] ?? 9)
    )

    sortedTasks.forEach(task => {
      if (task.status === 'completed' || task.status === 'cancelled') return

      if (!task.due_date) {
        laterTasksArray.push(task)
        return
      }
      
      // Extract date string from task.due_date regardless of format
      let taskDateString: string
      
      if (typeof task.due_date === 'string') {
        if (task.due_date.includes('T')) {
          // ISO format - extract date part
          taskDateString = task.due_date.split('T')[0]
        } else {
          // Already YYYY-MM-DD format
          taskDateString = task.due_date
        }
      } else {
        // Convert to YYYY-MM-DD
        const taskDate = new Date(task.due_date)
        taskDateString = formatLocalDate(taskDate)
      }
      
      // Validate date format
      if (!/^\d{4}-\d{2}-\d{2}$/.test(taskDateString)) {
        laterTasksArray.push(task)
        return
      }
      
      // Calculate day difference using string comparison for consistency
      const taskDate = new Date(taskDateString + 'T00:00:00')
      const todayDate = new Date(todayString + 'T00:00:00')
      const dayDiff = Math.round((taskDate.getTime() - todayDate.getTime()) / (1000 * 60 * 60 * 24))
      
      if (dayDiff < 0) {
        // Overdue tasks go to Today column
        workingColumns[0].tasks.push(task)
      } else if (dayDiff >= 0 && dayDiff < 6 && workingColumns[dayDiff]) {
        workingColumns[dayDiff].tasks.push(task)
      } else {
        laterTasksArray.push(task)
      }
    })

    setColumns(workingColumns)
    setLaterTasks(laterTasksArray)
  }

  const getTaskStatusColor = (task: Task): string => {
    if (!task.due_date) {
      return 'border-l-4 border-gray-900 bg-white dark:bg-gray-800' // Normal
    }
    
    // Use consistent date comparison logic
    const todayString = formatLocalDate(new Date())
    let taskDateString: string
    
    if (typeof task.due_date === 'string') {
      if (task.due_date.includes('T')) {
        taskDateString = task.due_date.split('T')[0]
      } else {
        taskDateString = task.due_date
      }
    } else {
      const taskDate = new Date(task.due_date)
      taskDateString = formatLocalDate(taskDate)
    }
    
    if (taskDateString < todayString) {
      return 'border-l-4 border-red-500 bg-red-50 dark:bg-red-900/20' // Overdue
    }
    
    if (taskDateString === todayString && task.status === 'pending') {
      return 'border-l-4 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/20' // Due today
    }
    
    return 'border-l-4 border-gray-900 bg-white dark:bg-gray-800' // Normal
  }

  const getPriorityColor = (priority: string): string => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400'
      case 'medium': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-gray-600'
      case 'low': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
    }
  }

  const getTaskDisplayName = (task: Task) => {
    // Priority: Customer > Lead > Deal > General Task
    if (task.customer_name) {
      return task.customer_name
    }
    if (task.lead_first_name || task.lead_last_name) {
      return `${task.lead_first_name || ''} ${task.lead_last_name || ''}`.trim()
    }
    if (task.deal_name) {
      return task.deal_name
    }
    return 'General Task'
  }

  const getTaskDisplayType = (task: Task) => {
    if (task.customer_name) return 'customer'
    if (task.lead_first_name || task.lead_last_name) return 'lead'
    if (task.deal_name) return 'deal'
    return 'general'
  }


  // Calendar helper functions
  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const firstDay = new Date(year, month, 1)
    const lastDay = new Date(year, month + 1, 0)
    const daysInMonth = lastDay.getDate()
    const startingDayOfWeek = firstDay.getDay()
    
    return { daysInMonth, startingDayOfWeek, year, month }
  }

  const getTasksForDate = (date: Date) => {
    const dateString = formatLocalDate(date)
    const allTasks = [...columns.flatMap(col => col.tasks), ...laterTasks]
    
    return allTasks.filter(task => {
      if (!task.due_date) return false
      
      let taskDateString: string
      if (typeof task.due_date === 'string') {
        taskDateString = task.due_date.includes('T') ? task.due_date.split('T')[0] : task.due_date
      } else {
        taskDateString = formatLocalDate(new Date(task.due_date))
      }
      
      return taskDateString === dateString
    })
  }

  const changeMonth = (increment: number) => {
    setCurrentMonth(prev => {
      const newDate = new Date(prev)
      newDate.setMonth(newDate.getMonth() + increment)
      return newDate
    })
  }

  const goToToday = () => {
    setCurrentMonth(new Date())
  }

  const closeLeadDetailModal = () => {
    setLeadDetailModal({ isOpen: false, leadId: null })
    setLeadDetailData(null)
    setLeadDetailLoading(false)
    setLeadDetailError(null)
  }

  const handleTaskComplete = (task: Task) => {
    setCompletionModal({ isOpen: true, task })
  }

  const handleCreateTask = async () => {
    if (!taskDescription.trim()) {
      return
    }

    try {
      const token = localStorage.getItem('token')
      if (!token) {
        throw new Error('Authentication required')
      }

      // Use selected date or custom date
      let dueDate = taskDueDate || customDate
      if (!dueDate) {
        // Default to tomorrow using local date
        const tomorrow = new Date()
        tomorrow.setDate(tomorrow.getDate() + 1)
        dueDate = formatLocalDate(tomorrow)
      }

      const taskData: any = {
        description: taskDescription,
        due_date: dueDate,
        due_time: hasSpecificTime && taskTime ? taskTime : null,
        has_specific_time: hasSpecificTime,
        priority: taskPriority,
        status: 'pending'
      }

      // Add customer_id only if a customer is selected
      if (selectedCustomer) {
        taskData.customer_id = selectedCustomer.id
      }

      // Add lead_id if a lead is selected
      if (selectedLead) {
        taskData.lead_id = selectedLead.id
      }

      // Add optional fields
      if (selectedDeal) {
        taskData.deal_id = selectedDeal.id
      }

      const response = await makeApiCall('api/tasks', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(taskData)
      })

      if (!response.ok) {
        throw new Error('Failed to create task')
      }

      // Refresh tasks to show the new task
      await fetchTasks()

      // Reset form and close modal
      resetCreateTaskModal()

    } catch (err) {
      console.error('Error creating task:', err)
      setError('Failed to create task')
    }
  }

  const resetCreateTaskModal = () => {
    setShowCreateTaskModal(false)
    // Small delay to prevent focus conflicts
    setTimeout(() => {
      setCustomerSearch('')
      setSelectedCustomer(null)
      setSelectedLead(null)
      setCustomerDeals([])
      setSelectedDeal(null)
      setTaskDescription('')
      setTaskPriority('medium')
      setTaskDueDate('')
      setCustomDate('')
      setTaskTime('')
      setHasSpecificTime(false)
    }, 100)
  }

  const handleCustomerSelect = async (customer: Customer) => {
    setSelectedCustomer(customer)
    setSelectedLead(null) // Clear lead selection when customer is selected
    setCustomerSearch(customer.name)
    
    // Fetch deals for this customer
    try {
      const token = localStorage.getItem('token')
      if (!token) return

      const response = await makeApiCall(`api/deals?customer_id=${customer.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      })

      if (response.ok) {
        const data = await response.json()
        setCustomerDeals(data.deals || [])
      }
    } catch (err) {
      console.error('Error fetching customer deals:', err)
    }
  }

  const handleLeadSelect = (lead: Lead) => {
    setSelectedLead(lead)
    setSelectedCustomer(null) // Clear customer selection when lead is selected
    setCustomerDeals([]) // Clear deals when lead is selected
    setSelectedDeal(null) // Clear deal selection
    const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 
                     lead.email || lead.phone || 'Unnamed Lead'
    setCustomerSearch(leadName)
  }

  const getNext7Days = () => {
    const days = []
    const today = new Date()
    
    for (let i = 0; i < 7; i++) {
      const date = new Date(today)
      date.setDate(today.getDate() + i)
      
      let label = ''
              if (i === 0) label = t('dashboard.periods.today')
        else if (i === 1) label = t('tasks.tomorrow')
      else label = date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })
      
      days.push({
        date: formatLocalDate(date),
        label,
        isToday: i === 0,
        isTomorrow: i === 1
      })
    }
    
    return days
  }

  const filteredCustomers = customers.filter(customer =>
    customer.name.toLowerCase().includes(customerSearch.toLowerCase()) ||
    (customer.email && customer.email.toLowerCase().includes(customerSearch.toLowerCase())) ||
    (customer.phone && customer.phone.includes(customerSearch))
  )

  const filteredLeads = leads.filter(lead => {
    const searchTerm = customerSearch.toLowerCase()
    const firstName = lead.first_name?.toLowerCase() || ''
    const lastName = lead.last_name?.toLowerCase() || ''
    const fullName = `${firstName} ${lastName}`.trim()
    const email = lead.email?.toLowerCase() || ''
    const phone = lead.phone || ''
    
    return firstName.includes(searchTerm) ||
           lastName.includes(searchTerm) ||
           fullName.includes(searchTerm) ||
           email.includes(searchTerm) ||
           phone.includes(searchTerm)
  })

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result

    // No destination or same position
    if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
      return
    }

    // Find the task being moved
    let movedTask: Task | null = null
    
    // Find task in source column
    if (source.droppableId === 'later') {
      movedTask = laterTasks.find(task => task.id.toString() === draggableId) || null
    } else if (source.droppableId.startsWith('day-')) {
      const sourceColumn = columns.find(col => col.id === source.droppableId)
      movedTask = sourceColumn?.tasks.find(task => task.id.toString() === draggableId) || null
    }

    if (!movedTask) return

    // Calculate new due date based on destination column
    const newDueDate = new Date()
    
    if (destination.droppableId === 'later') {
      // For "later" column, set to 7 days from now
      newDueDate.setDate(newDueDate.getDate() + 7)
    } else if (destination.droppableId.startsWith('day-')) {
      // For dated columns like "day-0", "day-1", etc.
      const dayOffset = parseInt(destination.droppableId.replace('day-', ''))
      if (!isNaN(dayOffset)) {
        newDueDate.setDate(newDueDate.getDate() + dayOffset)
      }
    }

    // Create updated task with new due date
    const updatedTask = {
      ...movedTask,
      due_date: formatLocalDate(newDueDate)
    }

    // Optimistically update UI immediately
    if (source.droppableId === 'later' && destination.droppableId !== 'later') {
      // Moving from later to a day column
      const newLaterTasks = laterTasks.filter(task => task.id.toString() !== draggableId)
      setLaterTasks(newLaterTasks)
      
      setColumns(prevColumns => 
        prevColumns.map(col => {
          if (col.id === destination.droppableId) {
            const newTasks = [...col.tasks]
            newTasks.splice(destination.index, 0, updatedTask)
            return { ...col, tasks: newTasks }
          }
          return col
        })
      )
    } else if (source.droppableId !== 'later' && destination.droppableId === 'later') {
      // Moving from a day column to later
      setColumns(prevColumns => 
        prevColumns.map(col => {
          if (col.id === source.droppableId) {
            const newTasks = col.tasks.filter(task => task.id.toString() !== draggableId)
            return { ...col, tasks: newTasks }
          }
          return col
        })
      )
      
      const newLaterTasks = [...laterTasks]
      newLaterTasks.splice(destination.index, 0, updatedTask)
      setLaterTasks(newLaterTasks)
    } else if (source.droppableId === destination.droppableId && source.droppableId !== 'later') {
      // Reordering within the same day column (vertical drag)
      setColumns(prevColumns =>
        prevColumns.map(col => {
          if (col.id !== source.droppableId) return col
          const newTasks = [...col.tasks]
          newTasks.splice(source.index, 1)
          newTasks.splice(destination.index, 0, updatedTask)
          return { ...col, tasks: newTasks }
        })
      )
      return // No due_date change — skip the backend update below
    } else if (source.droppableId !== 'later' && destination.droppableId !== 'later') {
      // Moving between different day columns
      setColumns(prevColumns => 
        prevColumns.map(col => {
          if (col.id === source.droppableId) {
            const newTasks = col.tasks.filter(task => task.id.toString() !== draggableId)
            return { ...col, tasks: newTasks }
          } else if (col.id === destination.droppableId) {
            const newTasks = [...col.tasks]
            newTasks.splice(destination.index, 0, updatedTask)
            return { ...col, tasks: newTasks }
          }
          return col
        })
      )
    } else {
      // Moving within later column (reordering)
      const newLaterTasks = [...laterTasks]
      newLaterTasks.splice(source.index, 1)
      newLaterTasks.splice(destination.index, 0, updatedTask)
      setLaterTasks(newLaterTasks)
    }

    // Update backend (fire and forget for smoother UX)
    try {
      const token = localStorage.getItem('token')
      const response = await makeApiCall(`api/tasks/${draggableId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          due_date: formatLocalDate(newDueDate)
        })
      })

      if (!response.ok) {
        console.error('Failed to update task on server')
        // Could revert the optimistic update here if needed
        // For now, we'll just log the error
      }
      
    } catch (error) {
      console.error('Error updating task:', error)
      // Could revert the optimistic update here if needed
    }
  }



  const formatDate = (date: Date): string => {
    return date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { weekday: 'short', month: 'short', day: 'numeric' })
  }

  const handleCardClick = (task: Task) => {
    if (task.deal_id) {
      navigate(`/dashboard/pipeline/${task.deal_id}`)
    } else if (task.lead_id) {
      setLeadDetailModal({ isOpen: true, leadId: task.lead_id })
    } else if (task.customer_id) {
      navigate(`/dashboard/customers/${task.customer_id}`)
    }
  }

  const renderTaskCard = (task: Task, index: number) => (
    <Draggable key={task.id} draggableId={task.id.toString()} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          onClick={() => handleCardClick(task)}
          className={`${getTaskStatusColor(task)} rounded-lg p-3 mb-3 shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 touch-manipulation ${
            snapshot.isDragging ? 'shadow-lg rotate-3' : ''
          }`}
        >
          <div className="space-y-2">
            {/* Task Description */}
            <div className="font-medium text-gray-900 dark:text-white text-sm line-clamp-2">
              {task.description}
            </div>

            {/* Associated Entity Info */}
            <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
              <CustomersIcon size={12} className="mr-1 flex-shrink-0" />
              <span className="truncate">
                {getTaskDisplayName(task)}
                {getTaskDisplayType(task) === 'lead' && (
                  <span className="ml-1 text-xs bg-blue-100 text-blue-800 px-1 rounded">LEAD</span>
                )}
                {getTaskDisplayType(task) === 'deal' && (
                  <span className="ml-1 text-xs bg-green-100 text-green-800 px-1 rounded">DEAL</span>
                )}
              </span>
            </div>

            {/* Priority Badge and Complete Button */}
            <div className="flex items-center justify-between">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(task.priority)}`}>
                {translatePriority(task.priority)}
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation()
                  handleTaskComplete(task)
                }}
                onTouchStart={(e) => e.stopPropagation()}
                className="px-2.5 py-1 text-xs font-semibold text-green-700 dark:text-green-400 bg-green-100 dark:bg-green-900/30 hover:bg-green-200 dark:hover:bg-green-900/60 rounded-lg transition-colors touch-manipulation"
              >
                Complete
              </button>
            </div>
          </div>
        </div>
      )}
    </Draggable>
  )

  const renderColumnHeader = (column: TaskColumn) => (
    <div key={`header-${column.id}`} className="flex-shrink-0 w-72 sm:w-80">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base truncate">
            {column.title}
          </h3>
          <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-gray-600 text-xs px-2 py-1 rounded-full flex-shrink-0">
            {column.tasks.length}
          </span>
        </div>
      </div>
    </div>
  )

  const renderColumnContent = (column: TaskColumn) => (
    <div key={column.id} className="flex-shrink-0 w-72 sm:w-80">
      <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 h-full border border-gray-200 dark:border-gray-700 shadow-sm">
        {/* Tasks */}
        <Droppable droppableId={column.id}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              className={`min-h-[300px] ${
                snapshot.isDraggingOver ? 'bg-blue-50 dark:bg-blue-900/20 rounded-lg p-2' : ''
              }`}
            >
              {column.tasks.map((task, index) => renderTaskCard(task, index))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </div>
    </div>
  )

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">{t('tasks.loadingBoard')}</span>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-4">
        <div className="text-red-800 dark:text-red-200">
          <strong>{t('common.error')}:</strong> {error}
        </div>
        <button
          onClick={fetchTasks}
          className="mt-2 text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline"
        >
          Retry
        </button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between space-y-4 sm:space-y-0">
        <div className="flex-1 min-w-0">
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mb-2 leading-tight">
            {t('tasks.boardTitle')}
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">
            {t('tasks.subtitle')}
          </p>
        </div>
        <div className="flex items-center gap-2 w-full sm:w-auto">
          {/* View mode icon switcher */}
          <div className="flex items-center rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            <button
              onClick={() => setViewMode('board')}
              title={t('tasks.boardView')}
              className={`p-2 transition-colors ${viewMode === 'board' ? 'bg-indigo-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM14 5a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1V5zM4 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1H5a1 1 0 01-1-1v-4zM14 15a1 1 0 011-1h4a1 1 0 011 1v4a1 1 0 01-1 1h-4a1 1 0 01-1-1v-4z" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('table')}
              title={t('tasks.tableView')}
              className={`p-2 transition-colors ${viewMode === 'table' ? 'bg-indigo-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              title={t('tasks.calendarView')}
              className={`p-2 transition-colors ${viewMode === 'calendar' ? 'bg-indigo-600 text-white' : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </button>
          </div>

          {/* Archive icon button */}
          <button
            onClick={() => setShowArchivedTasks(true)}
            title={t('tasks.viewArchived')}
            className="p-2 rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4" />
            </svg>
          </button>

          <button
            onClick={() => setShowCreateTaskModal(true)}
            className="flex items-center justify-center space-x-1 sm:space-x-2 bg-gradient-to-r from-gray-800 to-gray-900 text-white px-3 sm:px-4 py-2 rounded-lg text-sm sm:text-base font-semibold hover:shadow-lg transition-all duration-200 touch-manipulation flex-1 sm:flex-none"
          >
            <PlusIcon size={16} className="sm:w-5 sm:h-5" />
            <span>{t('tasks.createTask')}</span>
          </button>
        </div>
      </div>


      {/* Board View */}
      {viewMode === 'board' && (
      <DragDropContext onDragEnd={handleDragEnd}>
        <div className="overflow-x-auto widget-scrollbar">
          <div className="flex flex-col min-w-max pb-4">
            {/* Sticky Headers - Mobile Optimized */}
            <div className="sticky top-0 z-10 bg-white dark:bg-gray-900 pb-2 shadow-sm">
              <div className="flex space-x-3 sm:space-x-4 px-1 sm:px-2">
                {/* Main Column Headers */}
                {columns.map(column => renderColumnHeader(column))}

                {/* Later Column Header */}
                <div className="flex-shrink-0 w-72 sm:w-80">
                  <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 border border-gray-200 dark:border-gray-700 shadow-sm">
                    <div className="flex items-center justify-between">
                      <h3 className="font-semibold text-gray-900 dark:text-white text-sm sm:text-base">
                        {t('tasks.later')}
                      </h3>
                      <div className="flex items-center space-x-2">
                        <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-gray-600 text-xs px-2 py-1 rounded-full">
                          {laterTasks.length}
                        </span>
                        {laterTasks.length > 0 && (
                          <button
                            onClick={() => setShowLaterExpanded(!showLaterExpanded)}
                            className="text-gray-900 dark:text-gray-600 hover:text-purple-800 dark:hover:text-purple-200 text-xs touch-manipulation"
                          >
                            {showLaterExpanded ? t('tasks.collapse') : t('tasks.expand')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Content Area - Mobile Optimized */}
            <div className="flex space-x-3 sm:space-x-4 px-1 sm:px-2">
              {/* Main Column Contents */}
              {columns.map(column => renderColumnContent(column))}

              {/* Later Column Content */}
              <div className="flex-shrink-0 w-72 sm:w-80">
                <div className="bg-white dark:bg-gray-800 rounded-xl p-3 sm:p-4 h-full border border-gray-200 dark:border-gray-700 shadow-sm">
                  <Droppable droppableId="later">
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.droppableProps}
                        className={`space-y-2 min-h-[300px] ${
                          snapshot.isDraggingOver ? 'bg-purple-50 dark:bg-purple-900/20 rounded-lg p-2' : ''
                        }`}
                      >
                        {(showLaterExpanded ? laterTasks : laterTasks.slice(0, 10)).map((task, index) => renderTaskCard(task, index))}
                        {!showLaterExpanded && laterTasks.length > 10 && (
                          <div className="text-center text-gray-500 dark:text-gray-400 text-sm py-2">
                            +{laterTasks.length - 10} {t('tasks.moreTasks')}
                          </div>
                        )}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              </div>
            </div>
          </div>
        </div>
      </DragDropContext>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto widget-scrollbar">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('tasks.task')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('tasks.associatedWith')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('tasks.dueDate')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('tasks.priority')}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('tasks.status')}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    {t('tasks.actions')}
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {/* Today's Tasks */}
                {columns[0]?.tasks.length > 0 && (
                  <>
                    <tr className="bg-gray-100 dark:bg-gray-700">
                      <td colSpan={6} className="px-6 py-2 text-sm font-semibold text-gray-900 dark:text-white">
                        {t('dashboard.periods.today')} ({columns[0].tasks.length})
                      </td>
                    </tr>
                    {columns[0].tasks.map((task) => (
                      <tr
                        key={task.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                        onClick={() => handleCardClick(task)}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {task.description}
                          </div>
                          {task.due_time && task.has_specific_time && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              🕐 {task.due_time}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center text-sm text-gray-900 dark:text-white">
                            <CustomersIcon size={14} className="mr-2 flex-shrink-0" />
                            <span className="truncate">{getTaskDisplayName(task)}</span>
                          </div>
                          {getTaskDisplayType(task) === 'lead' && (
                            <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">LEAD</span>
                          )}
                          {getTaskDisplayType(task) === 'deal' && (
                            <span className="inline-block mt-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">DEAL</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {new Date(task.due_date).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                            {translatePriority(task.priority)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400">
                            {t('tasks.dueToday')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleTaskComplete(task)
                            }}
                            className="text-green-600 hover:text-green-900 dark:hover:text-green-400 transition-colors"
                          >
                            <span className="font-semibold">Complete</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Tomorrow's Tasks */}
                {columns[1]?.tasks.length > 0 && (
                  <>
                    <tr className="bg-gray-100 dark:bg-gray-700">
                      <td colSpan={6} className="px-6 py-2 text-sm font-semibold text-gray-900 dark:text-white">
                        {t('tasks.tomorrow')} ({columns[1].tasks.length})
                      </td>
                    </tr>
                    {columns[1].tasks.map((task) => (
                      <tr
                        key={task.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                        onClick={() => handleCardClick(task)}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {task.description}
                          </div>
                          {task.due_time && task.has_specific_time && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              🕐 {task.due_time}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center text-sm text-gray-900 dark:text-white">
                            <CustomersIcon size={14} className="mr-2 flex-shrink-0" />
                            <span className="truncate">{getTaskDisplayName(task)}</span>
                          </div>
                          {getTaskDisplayType(task) === 'lead' && (
                            <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">LEAD</span>
                          )}
                          {getTaskDisplayType(task) === 'deal' && (
                            <span className="inline-block mt-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">DEAL</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {new Date(task.due_date).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                            {translatePriority(task.priority)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            {t('tasks.upcoming')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleTaskComplete(task)
                            }}
                            className="text-green-600 hover:text-green-900 dark:hover:text-green-400 transition-colors"
                          >
                            <span className="font-semibold">Complete</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Other Days */}
                {columns.slice(2).map((column, idx) => (
                  column.tasks.length > 0 && (
                    <React.Fragment key={column.id}>
                      <tr className="bg-gray-100 dark:bg-gray-700">
                        <td colSpan={6} className="px-6 py-2 text-sm font-semibold text-gray-900 dark:text-white">
                          {column.title} ({column.tasks.length})
                        </td>
                      </tr>
                      {column.tasks.map((task) => (
                        <tr
                          key={task.id}
                          className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                          onClick={() => handleCardClick(task)}
                        >
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 dark:text-white">
                              {task.description}
                            </div>
                            {task.due_time && task.has_specific_time && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                                🕐 {task.due_time}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center text-sm text-gray-900 dark:text-white">
                              <CustomersIcon size={14} className="mr-2 flex-shrink-0" />
                              <span className="truncate">{getTaskDisplayName(task)}</span>
                            </div>
                            {getTaskDisplayType(task) === 'lead' && (
                              <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">LEAD</span>
                            )}
                            {getTaskDisplayType(task) === 'deal' && (
                              <span className="inline-block mt-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">DEAL</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900 dark:text-white">
                              {new Date(task.due_date).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { 
                                weekday: 'short', 
                                month: 'short', 
                                day: 'numeric' 
                              })}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                              {translatePriority(task.priority)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400">
                              {t('tasks.scheduled')}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                handleTaskComplete(task)
                              }}
                              className="text-green-600 hover:text-green-900 dark:hover:text-green-400 transition-colors"
                            >
                              <span className="font-semibold">Complete</span>
                            </button>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  )
                ))}

                {/* Later Tasks */}
                {laterTasks.length > 0 && (
                  <>
                    <tr className="bg-gray-100 dark:bg-gray-700">
                      <td colSpan={6} className="px-6 py-2 text-sm font-semibold text-gray-900 dark:text-white">
                        {t('tasks.later')} ({laterTasks.length})
                      </td>
                    </tr>
                    {laterTasks.map((task) => (
                      <tr
                        key={task.id}
                        className="hover:bg-gray-50 dark:hover:bg-gray-700/50 cursor-pointer transition-colors"
                        onClick={() => handleCardClick(task)}
                      >
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {task.description}
                          </div>
                          {task.due_time && task.has_specific_time && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                              🕐 {task.due_time}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center text-sm text-gray-900 dark:text-white">
                            <CustomersIcon size={14} className="mr-2 flex-shrink-0" />
                            <span className="truncate">{getTaskDisplayName(task)}</span>
                          </div>
                          {getTaskDisplayType(task) === 'lead' && (
                            <span className="inline-block mt-1 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded">LEAD</span>
                          )}
                          {getTaskDisplayType(task) === 'deal' && (
                            <span className="inline-block mt-1 text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded">DEAL</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900 dark:text-white">
                            {task.due_date ? new Date(task.due_date).toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { 
                              weekday: 'short', 
                              month: 'short', 
                              day: 'numeric' 
                            }) : t('tasks.noDate')}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getPriorityColor(task.priority)}`}>
                            {translatePriority(task.priority)}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-400">
                            {t('tasks.later')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              handleTaskComplete(task)
                            }}
                            className="text-green-600 hover:text-green-900 dark:hover:text-green-400 transition-colors"
                          >
                            <span className="font-semibold">Complete</span>
                          </button>
                        </td>
                      </tr>
                    ))}
                  </>
                )}

                {/* Empty State */}
                {columns.every(col => col.tasks.length === 0) && laterTasks.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-6 py-12 text-center">
                      <div className="text-gray-500 dark:text-gray-400">
                        <p className="text-lg font-medium mb-2">{t('tasks.noTasks')}</p>
                        <p className="text-sm">{t('tasks.createFirstTask')}</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Calendar Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
            <button
              onClick={() => changeMonth(-1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            
            <div className="flex items-center space-x-3">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {currentMonth.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { month: 'long', year: 'numeric' })}
              </h2>
              <button
                onClick={goToToday}
                className="px-3 py-1 text-sm bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-200 dark:hover:bg-blue-900/50 transition-colors font-medium"
              >
                {t('dashboard.periods.today')}
              </button>
            </div>
            
            <button
              onClick={() => changeMonth(1)}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Calendar and Side Panel Container */}
          <div className="flex">
            {/* Calendar Grid */}
            <div className={`p-4 transition-all duration-300 ${dateTasksModal.isOpen ? 'w-[71.4%]' : 'w-full'}`}>
              {/* Day Headers */}
              <div className="grid grid-cols-7 gap-2 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, idx) => (
                  <div key={day} className="text-center text-xs font-semibold text-gray-600 dark:text-gray-400 py-2">
                    {language === 'fr' ? ['Dim', 'Lun', 'Mar', 'Mer', 'Jeu', 'Ven', 'Sam'][idx] : day}
                  </div>
                ))}
              </div>

              {/* Calendar Days */}
              <div className="grid grid-cols-7 gap-2">
              {(() => {
                const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth)
                const days = []
                const today = new Date()
                const todayString = formatLocalDate(today)

                // Empty cells for days before month starts
                for (let i = 0; i < startingDayOfWeek; i++) {
                  days.push(
                    <div key={`empty-${i}`} className="aspect-square min-h-[100px] bg-gray-50 dark:bg-gray-900/50 rounded-lg" />
                  )
                }

                // Days of the month
                for (let day = 1; day <= daysInMonth; day++) {
                  const date = new Date(year, month, day)
                  const dateString = formatLocalDate(date)
                  const isToday = dateString === todayString
                  const dayTasks = getTasksForDate(date)
                  const isSelected = dateTasksModal.isOpen && dateTasksModal.date && formatLocalDate(dateTasksModal.date) === dateString

                  days.push(
                    <div
                      key={day}
                      onClick={() => {
                        if (dayTasks.length > 0) {
                          setDateTasksModal({ isOpen: true, date, tasks: dayTasks })
                        }
                      }}
                      className={`aspect-square min-h-[100px] rounded-lg border-2 p-2 transition-all ${
                        dayTasks.length > 0 ? 'cursor-pointer' : 'cursor-default'
                      } ${
                        isToday
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : isSelected
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600 bg-white dark:bg-gray-800'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <div className={`text-sm font-semibold ${
                          isToday || isSelected ? 'text-blue-600 dark:text-blue-400' : 'text-gray-900 dark:text-white'
                        }`}>
                          {day}
                        </div>
                        {dayTasks.length > 0 && (
                          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-blue-500 text-white text-xs font-bold">
                            {dayTasks.length > 9 ? '9+' : dayTasks.length}
                          </div>
                        )}
                      </div>
                      <div className="space-y-1 overflow-y-auto max-h-[70px] widget-scrollbar">
                        {dayTasks.slice(0, 2).map(task => (
                          <div
                            key={task.id}
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCardClick(task)
                            }}
                            className={`text-xs px-1.5 py-0.5 rounded truncate ${getPriorityColor(task.priority)}`}
                          >
                            {task.description}
                          </div>
                        ))}
                        {dayTasks.length > 2 && (
                          <div className="text-xs text-gray-600 dark:text-gray-400 px-1.5 font-medium">
                            +{dayTasks.length - 2} more...
                          </div>
                        )}
                      </div>
                    </div>
                  )
                }

                return days
              })()}
            </div>
          </div>

          {/* Side Panel for Selected Date Tasks */}
          {dateTasksModal.isOpen && dateTasksModal.date && (
            <div className="w-[28.6%] border-l border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/30 p-4 overflow-y-auto max-h-[800px] widget-scrollbar">
              {/* Side Panel Header */}
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                    {dateTasksModal.date.toLocaleDateString(language === 'fr' ? 'fr-CA' : 'en-US', { 
                      weekday: 'long',
                      day: 'numeric'
                    })}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-0.5">
                    {dateTasksModal.tasks.length} {dateTasksModal.tasks.length === 1 ? t('tasks.task') : t('tasks.tasks')}
                  </p>
                </div>
                <button
                  onClick={() => setDateTasksModal({ isOpen: false, date: null, tasks: [] })}
                  className="p-1.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                >
                  <svg className="w-5 h-5 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Tasks List */}
              <div className="space-y-3">
                {dateTasksModal.tasks.map(task => (
                  <div
                    key={task.id}
                    onClick={() => {
                      handleCardClick(task)
                    }}
                    className="bg-white dark:bg-gray-800 rounded-lg p-3 cursor-pointer hover:shadow-md dark:hover:bg-gray-750 transition-all border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center space-x-2 flex-wrap gap-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${getPriorityColor(task.priority)}`}>
                          {translatePriority(task.priority)}
                        </span>
                        {task.due_time && task.has_specific_time && (
                          <span className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                            <svg className="w-3.5 h-3.5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            {task.due_time}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleTaskComplete(task)
                        }}
                        className="text-green-600 hover:text-green-700 dark:hover:text-green-400 transition-colors flex-shrink-0"
                      >
                        <span className="font-semibold">Complete</span>
                      </button>
                    </div>
                    
                    <div className="text-sm font-medium text-gray-900 dark:text-white mb-2 line-clamp-2">
                      {task.description}
                    </div>
                    
                    <div className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                      <CustomersIcon size={12} className="mr-1.5 flex-shrink-0" />
                      <span className="truncate">{getTaskDisplayName(task)}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      <CompleteTaskModal
        isOpen={completionModal.isOpen}
        task={completionModal.task}
        onClose={() => setCompletionModal({ isOpen: false, task: null })}
        onRefresh={fetchTasks}
      />

      {/* Task Creation Modal - Mobile Optimized */}
      {showCreateTaskModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-2xl shadow-2xl max-h-[95vh] overflow-hidden border border-gray-200 dark:border-gray-700">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-gray-800 to-gray-900 p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-2xl font-bold mb-2">{t('tasks.modals.quickTask')}</h3>
                  <p className="text-blue-100 text-sm">{t('tasks.modals.createTaskDescription', { defaultValue: 'Create a new task and assign it to a customer or lead' })}</p>
                </div>
                <button
                  onClick={resetCreateTaskModal}
                  className="p-2 hover:bg-white/20 rounded-full transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            
            {/* Modal Body */}
            <div className="p-8 overflow-y-auto widget-scrollbar max-h-[calc(95vh-120px)]">
            <div className="space-y-6">
              {/* Task Description */}
              <div className="group">
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-blue-500 rounded-full mr-2"></span>
                  {t('tasks.modals.whatNeedsToBeDone')} *
                </label>
                <textarea
                  autoFocus
                  value={taskDescription}
                  onChange={(e) => setTaskDescription(e.target.value)}
                  placeholder={t('tasks.modals.taskDescriptionPlaceholder', { defaultValue: 'e.g., Follow up on mortgage application' })}
                  className="w-full px-4 py-3 text-lg border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200 resize-none"
                  rows={3}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && e.metaKey) {
                      e.preventDefault()
                      handleCreateTask()
                    }
                  }}
                />
              </div>

              {/* Priority Selection */}
              <div className="group">
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                  {t('tasks.modals.priority')}
                </label>
                <div className="flex flex-wrap gap-3">
                  {(['low', 'medium', 'high'] as const).map((priority) => (
                    <button
                      key={priority}
                      type="button"
                      onClick={() => setTaskPriority(priority)}
                      className={`flex-1 min-w-[100px] px-4 py-3 rounded-xl font-semibold text-sm transition-all duration-200 ${
                        taskPriority === priority
                          ? priority === 'high' 
                            ? 'bg-gradient-to-r from-red-500 to-red-600 text-white shadow-lg scale-105' 
                            : priority === 'medium'
                            ? 'bg-gradient-to-r from-yellow-500 to-yellow-600 text-white shadow-lg scale-105'
                            : 'bg-gradient-to-r from-green-500 to-green-600 text-white shadow-lg scale-105'
                          : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 border-2 border-gray-200 dark:border-gray-600'
                      }`}
                    >
                      {translatePriority(priority)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Customer Search */}
              <div className="group">
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                  {t('tasks.modals.searchCustomerOrLead')} *
                </label>
                <div className="relative">
                  <input
                    type="text"
                    value={customerSearch}
                    onChange={(e) => {
                      setCustomerSearch(e.target.value)
                      if (!e.target.value) {
                        setSelectedCustomer(null)
                        setSelectedLead(null)
                        setCustomerDeals([])
                        setSelectedDeal(null)
                      }
                    }}
                    placeholder={t('tasks.modals.searchCustomerOrLeadPlaceholder', { defaultValue: 'Search by name, email, or phone...' })}
                    className="w-full px-4 py-3 pl-12 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all duration-200 placeholder-gray-400 dark:placeholder-gray-500"
                  />
                  <svg className="absolute left-4 top-3.5 w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                  
                  {/* Search Results Dropdown */}
                  {customerSearch && !selectedCustomer && !selectedLead && (filteredCustomers.length > 0 || filteredLeads.length > 0) && (
                    <div className="absolute z-20 w-full mt-2 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-xl shadow-2xl max-h-60 overflow-y-auto widget-scrollbar">
                      {/* Customers Section */}
                      {filteredCustomers.length > 0 && (
                        <>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide bg-gray-50 dark:bg-gray-600">
                            {t('tasks.modals.customers')}
                          </div>
                          {filteredCustomers.slice(0, 3).map((customer) => (
                            <button
                              key={`customer-${customer.id}`}
                              onClick={() => handleCustomerSelect(customer)}
                              className="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-gray-600 focus:bg-blue-50 dark:focus:bg-gray-600 focus:outline-none border-b border-gray-100 dark:border-gray-600 last:border-b-0 transition-colors"
                            >
                              <div className="font-semibold text-gray-900 dark:text-white">{customer.name}</div>
                              <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {customer.email} {customer.phone && `• ${customer.phone}`}
                              </div>
                            </button>
                          ))}
                        </>
                      )}
                      
                      {/* Leads Section */}
                      {filteredLeads.length > 0 && (
                        <>
                          <div className="px-4 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide bg-gray-50 dark:bg-gray-600">
                            {t('tasks.modals.leads')}
                          </div>
                          {filteredLeads.slice(0, 3).map((lead) => {
                            const leadName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || 'Unnamed Lead'
                            return (
                              <button
                                key={`lead-${lead.id}`}
                                onClick={() => handleLeadSelect(lead)}
                                className="w-full px-4 py-3 text-left hover:bg-blue-50 dark:hover:bg-gray-600 focus:bg-blue-50 dark:focus:bg-gray-600 focus:outline-none border-b border-gray-100 dark:border-gray-600 last:border-b-0 transition-colors"
                              >
                                <div className="font-semibold text-gray-900 dark:text-white">
                                  {leadName} <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded ml-2">LEAD</span>
                                </div>
                                <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                  {lead.email} {lead.phone && `• ${lead.phone}`} • {lead.status_name}
                                </div>
                              </button>
                            )
                          })}
                        </>
                      )}
                    </div>
                  )}
                </div>

                {/* Selected Customer Display */}
                {selectedCustomer && (
                  <div className="mt-2 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-green-800 dark:text-green-200">{selectedCustomer.name}</div>
                        <div className="text-sm text-green-600 dark:text-green-300">{selectedCustomer.email}</div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedCustomer(null)
                          setCustomerSearch('')
                          setCustomerDeals([])
                          setSelectedDeal(null)
                        }}
                        className="text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-200"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}

                {/* Selected Lead Display */}
                {selectedLead && (
                  <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="font-medium text-blue-800 dark:text-blue-200">
                          {`${selectedLead.first_name || ''} ${selectedLead.last_name || ''}`.trim() || 'Unnamed Lead'}
                          <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded ml-2">LEAD</span>
                        </div>
                        <div className="text-sm text-gray-900 dark:text-blue-300">
                          {selectedLead.email} {selectedLead.phone && `• ${selectedLead.phone}`} • {selectedLead.status_name}
                        </div>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedLead(null)
                          setCustomerSearch('')
                        }}
                        className="text-gray-900 dark:text-gray-600 hover:text-blue-800 dark:hover:text-blue-200"
                      >
                        ✕
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Deal Cards */}
              {selectedCustomer && customerDeals.length > 0 && (
                <div className="group">
                  <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                    <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
                    {t('tasks.modals.linkToDeal')}
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {customerDeals.map((deal) => (
                      <button
                        key={deal.id}
                        onClick={() => setSelectedDeal(deal.id === selectedDeal?.id ? null : deal)}
                        className={`p-4 border-2 rounded-xl text-left transition-all ${
                          selectedDeal?.id === deal.id
                            ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20 shadow-md'
                            : 'border-gray-200 dark:border-gray-600 hover:border-gray-300 dark:hover:border-gray-500 hover:shadow-sm'
                        }`}
                      >
                        <div className="font-semibold text-gray-900 dark:text-white truncate">{deal.name}</div>
                        <div className="text-sm text-gray-500 dark:text-gray-400 mt-1">{deal.customer_name}</div>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Date Selection */}
              <div className="group">
                <label className="block text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3 flex items-center">
                  <span className="w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                  {t('tasks.modals.whenIsThisDue')}
                </label>
                
                {/* Quick Date Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2 mb-4">
                  {getNext7Days().map((day) => (
                    <button
                      key={day.date}
                      onClick={() => {
                        setTaskDueDate(day.date)
                        setCustomDate('')
                      }}
                      className={`p-3 border-2 rounded-xl text-center transition-all ${
                        taskDueDate === day.date
                          ? 'border-orange-500 bg-orange-50 dark:bg-orange-900/20 shadow-md'
                          : day.isToday
                          ? 'border-orange-300 bg-orange-50 dark:bg-orange-900/20 hover:border-orange-400'
                          : day.isTomorrow
                          ? 'border-green-300 bg-green-50 dark:bg-green-900/20 hover:border-green-400'
                          : 'border-gray-200 dark:border-gray-600 hover:border-gray-300'
                      }`}
                    >
                      <div className={`text-xs font-medium ${
                        taskDueDate === day.date ? 'text-orange-700 dark:text-orange-300' :
                        day.isToday ? 'text-orange-700 dark:text-orange-300' :
                        day.isTomorrow ? 'text-green-700 dark:text-green-300' :
                        'text-gray-600 dark:text-gray-400'
                      }`}>
                        {day.label}
                      </div>
                    </button>
                  ))}
                </div>

                {/* Custom Date Input */}
                <div className="flex items-center space-x-3">
                  <span className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.modals.orPickDate')}</span>
                  <input
                    type="date"
                    value={customDate}
                    onChange={(e) => {
                      setCustomDate(e.target.value)
                      setTaskDueDate('')
                    }}
                    className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Time Selection */}
              <div className="group">
                <div className="flex items-center space-x-3 mb-3">
                  <input
                    type="checkbox"
                    id="hasSpecificTime"
                    checked={hasSpecificTime}
                    onChange={(e) => {
                      setHasSpecificTime(e.target.checked)
                      if (!e.target.checked) {
                        setTaskTime('')
                      }
                    }}
                    className="h-4 w-4 text-gray-900 focus:ring-gray-900 border-gray-300 dark:border-gray-600 rounded"
                  />
                  <label htmlFor="hasSpecificTime" className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center">
                    <span className="w-2 h-2 bg-gray-500 rounded-full mr-2"></span>
                    {t('tasks.modals.setSpecificTime')}
                  </label>
                </div>
                
                {hasSpecificTime && (
                  <div className="flex items-center space-x-3 ml-6">
                    <span className="text-sm text-gray-500 dark:text-gray-400">{t('tasks.modals.time')}</span>
                    <input
                      type="time"
                      value={taskTime}
                      onChange={(e) => setTaskTime(e.target.value)}
                      className="px-3 py-2 border-2 border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-gray-500 focus:border-gray-500 transition-all"
                      step="900"
                    />
                    <span className="text-xs text-gray-400">
                      {t('tasks.modals.fifteenMinuteSlot')}
                    </span>
                  </div>
                )}
              </div>

              {/* Form Buttons */}
              <div className="flex gap-4 pt-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  type="button"
                  onClick={resetCreateTaskModal}
                  className="flex-1 px-6 py-3 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 font-semibold text-lg"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleCreateTask}
                  disabled={!taskDescription.trim() || (!selectedCustomer && !selectedLead)}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-gray-800 to-gray-900 text-white rounded-xl font-bold text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {t('tasks.modals.createTask')}
                </button>
              </div>
            </div>
            </div>
          </div>
        </div>
      )}


      {/* Lead Detail Modal */}
      {leadDetailModal.isOpen && leadDetailModal.leadId && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={closeLeadDetailModal}>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 w-full max-w-4xl shadow-2xl max-h-[90vh] overflow-y-auto widget-scrollbar" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                {t('leads.leadDetails')}
              </h3>
              <button
                onClick={closeLeadDetailModal}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
              >
                ✕
              </button>
            </div>

            {leadDetailLoading && (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
                <p className="text-gray-600 dark:text-gray-400">{t('tasks.modals.loadingLeadDetails')}</p>
              </div>
            )}

            {leadDetailError && (
              <div className="text-center py-8">
                <div className="text-red-600 dark:text-red-400 mb-4">
                  ⚠️ {leadDetailError}
                </div>
                <button
                  onClick={closeLeadDetailModal}
                  className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                >
                  Close
                </button>
              </div>
            )}

            {leadDetailData && !leadDetailLoading && !leadDetailError && (
              <div className="space-y-6">
                {/* Lead Header */}
                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {[leadDetailData.first_name, leadDetailData.last_name].filter(Boolean).join(' ') || 'Unnamed Lead'}
                      </h2>
                      <div className="flex items-center space-x-3 mt-2">
                        {leadDetailData.status_name && (
                          <span 
                            className="px-3 py-1 rounded-full text-sm font-medium text-white"
                            style={{ backgroundColor: leadDetailData.status_color || '#6B7280' }}
                          >
                            {leadDetailData.status_name}
                          </span>
                        )}
                        {leadDetailData.urgency_name && (
                          <span 
                            className="px-3 py-1 rounded-full text-sm font-medium text-white"
                            style={{ backgroundColor: leadDetailData.urgency_color || '#EF4444' }}
                          >
                            {leadDetailData.urgency_name}
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      Lead ID: {leadDetailData.id}
                    </div>
                  </div>
                </div>

                {/* Contact Information */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Contact Information</h3>
                    <div className="space-y-2">
                      {leadDetailData.email && (
                        <div className="flex items-center">
                          <span className="text-gray-600 dark:text-gray-400 w-16">Email:</span>
                          <a href={`mailto:${leadDetailData.email}`} className="text-gray-900 dark:text-gray-600 hover:underline">
                            {leadDetailData.email}
                          </a>
                        </div>
                      )}
                      {leadDetailData.phone && (
                        <div className="flex items-center">
                          <span className="text-gray-600 dark:text-gray-400 w-16">Phone:</span>
                          <a href={`tel:${leadDetailData.phone}`} className="text-gray-900 dark:text-gray-600 hover:underline">
                            {leadDetailData.phone}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Lead Information</h3>
                    <div className="space-y-2">
                      <div className="flex items-center">
                        <span className="text-gray-600 dark:text-gray-400 w-20">Created:</span>
                        <span className="text-gray-900 dark:text-white">
                          {new Date(leadDetailData.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      {leadDetailData.creator_first_name && (
                        <div className="flex items-center">
                          <span className="text-gray-600 dark:text-gray-400 w-20">Created by:</span>
                          <span className="text-gray-900 dark:text-white">
                            {leadDetailData.creator_first_name} {leadDetailData.creator_last_name}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Description */}
                {leadDetailData.description && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">Description</h3>
                    <p className="text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                      {leadDetailData.description}
                    </p>
                  </div>
                )}

                {/* Related Tasks */}
                {leadDetailData.tasks && leadDetailData.tasks.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                      Related Tasks ({leadDetailData.tasks.length})
                    </h3>
                    <div className="space-y-2">
                      {leadDetailData.tasks.map((task: any) => (
                        <div key={task.id} className="flex items-center justify-between p-2 bg-white dark:bg-gray-600 rounded">
                          <div>
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {task.description}
                            </span>
                          </div>
                          <div className="flex items-center space-x-2 text-xs">
                            <span className="text-gray-600 dark:text-gray-400">
                              {new Date(task.due_date).toLocaleDateString()}
                            </span>
                            <span className={`px-2 py-1 rounded ${
                              task.status === 'completed' ? 'bg-green-100 text-green-800' :
                              task.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {task.status}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Notes */}
                {leadDetailData.notes && leadDetailData.notes.length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-3">
                      Notes ({leadDetailData.notes.length})
                    </h3>
                    <div className="space-y-3">
                      {leadDetailData.notes.map((note: any) => (
                        <div key={note.id} className="p-3 bg-white dark:bg-gray-600 rounded">
                          <div className="flex items-center justify-between mb-2">
                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                              {note.creator_first_name} {note.creator_last_name}
                            </span>
                            <span className="text-xs text-gray-600 dark:text-gray-400">
                              {new Date(note.created_at).toLocaleDateString()}
                            </span>
                          </div>
                          <p className="text-sm text-gray-700 dark:text-gray-300 whitespace-pre-wrap">
                            {note.content}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={closeLeadDetailModal}
                className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Push Back Task Modal */}

      {/* Following Task Modal */}

      {/* Archived Tasks Modal */}
      <ArchivedTasks
        isOpen={showArchivedTasks}
        onClose={() => setShowArchivedTasks(false)}
      />
    </div>
  )
}

export default TasksBoard 